// Retreat Promotion Component - Reusable CTA for retreat pages
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Sparkles } from "lucide-react";

// Full-width retreat promotion banner
export const RetreatPromoBanner = ({ variant = "default" }) => {
  const variants = {
    default: {
      bg: "bg-gradient-to-r from-gold/10 via-gold/5 to-gold/10",
      border: "border-gold/20"
    },
    dark: {
      bg: "bg-neutral-800",
      border: "border-neutral-700"
    },
    highlight: {
      bg: "bg-gradient-to-r from-amber-50 to-orange-50",
      border: "border-gold/30"
    }
  };

  const style = variants[variant] || variants.default;
  const isDark = variant === "dark";

  return (
    <section className={`py-10 px-6 md:px-12 ${style.bg}`}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`p-6 md:p-8 rounded-2xl border ${style.border} ${isDark ? '' : 'bg-white/50'}`}
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className={`w-16 h-16 ${isDark ? 'bg-gold/20' : 'bg-gold/10'} rounded-2xl flex items-center justify-center`}>
                <Sparkles className={`w-8 h-8 ${isDark ? 'text-gold' : 'text-gold'}`} />
              </div>
            </div>
            <div className="flex-grow text-center md:text-left">
              <h3 className={`font-heading text-xl md:text-2xl ${isDark ? 'text-white' : 'text-neutral-800'} mb-2`}>
                Looking for a Deeper Experience?
              </h3>
              <p className={`${isDark ? 'text-neutral-300' : 'text-neutral-600'} text-sm md:text-base`}>
                Join our yoga retreats — from day retreats in Frome to international yoga holidays. 
                Immerse yourself in yoga, sound healing, and complete restoration.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link 
                to="/retreats/yoga-retreats-uk"
                className={`inline-flex items-center gap-2 ${isDark ? 'bg-gold hover:bg-gold-dark text-white' : 'bg-gold hover:bg-gold-dark text-white'} px-6 py-3 rounded-full font-medium transition-all text-sm whitespace-nowrap`}
              >
                Explore Retreats
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Compact inline retreat CTA for sidebars or smaller spaces
export const RetreatPromoCompact = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-gradient-to-br from-gold/10 to-gold/5 p-5 rounded-xl border border-gold/20"
  >
    <div className="flex items-center gap-3 mb-3">
      <Calendar className="w-5 h-5 text-gold" />
      <span className="text-xs uppercase tracking-wider text-gold font-semibold">
        Yoga Retreats
      </span>
    </div>
    <p className="text-neutral-700 text-sm mb-4">
      Step away from everyday life with our day retreats, weekend breaks, and international yoga holidays.
    </p>
    <Link 
      to="/retreats/yoga-retreats-uk"
      className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
    >
      View Retreats <ArrowRight className="w-4 h-4" />
    </Link>
  </motion.div>
);

// Card-style retreat promo for grids
export const RetreatPromoCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-white/80 p-6 rounded-2xl border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all"
  >
    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-4">
      <Sparkles className="w-6 h-6 text-gold" />
    </div>
    <h3 className="font-heading text-lg text-neutral-800 mb-2">
      Yoga Retreats UK
    </h3>
    <p className="text-neutral-600 text-sm mb-4">
      Day retreats in Frome, weekend yoga breaks, and international yoga holidays. 
      The perfect escape for body and mind.
    </p>
    <Link 
      to="/retreats/yoga-retreats-uk"
      className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-white px-5 py-2.5 rounded-full font-medium transition-colors text-sm"
    >
      Explore Retreats
      <ArrowRight className="w-4 h-4" />
    </Link>
  </motion.div>
);

// Floating sticky CTA for bottom of page
export const RetreatPromoSticky = ({ show = true }) => {
  if (!show) return null;
  
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-auto z-40"
    >
      <Link 
        to="/retreats/yoga-retreats-uk"
        className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-white px-6 py-3 rounded-full font-medium shadow-lg transition-all text-sm md:text-base"
      >
        <Calendar className="w-4 h-4" />
        View Upcoming Retreats
        <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
};

export default RetreatPromoBanner;
